/**
 * Mapping between IPA symbols (used in story content) and Kaldi phonemes (returned by PlacementManager)
 * This is needed for UpAndDown activities which now use Kaldi instead of PhonemeASR
 */

// IPA to Kaldi mapping
export const IPA_TO_KALDI = {
  // Consonants
  'b': 'b_phon',
  'd': 'd_phon',
  'f': 'f_phon',
  'g': 'g_phon',
  'h': 'hh_phon',
  'j': 'y_phon',
  'k': 'k_phon',
  'ks': 'ks_phon',
  'kw': 'kw_phon',
  'l': 'l_phon',
  'm': 'm_phon',
  'n': 'n_phon',
  'p': 'p_phon',
  'r': 'r_phon',
  's': 's_phon',
  't': 't_phon',
  'v': 'v_phon',
  'w': 'w_phon',
  'z': 'z_phon',
  
  // Vowels (IPA symbols)
  'æ': 'ae_phon',
  'ɑ': 'aa_phon',
  'ɛ': 'eh_phon',
  'ɪ': 'ih_phon',
  'ʊ': 'ah_phon',
  'ʌ': 'ah_phon',
  
  // Special consonants
  'ɹ': 'r_phon',
  'ʃ': 'sh_phon',
  'ʤ': 'jh_phon',
  'ʧ': 'ch_phon',
  'θ': 'th_phon',
};

// Kaldi to IPA mapping (reverse lookup)
export const KALDI_TO_IPA = Object.fromEntries(
  Object.entries(IPA_TO_KALDI).map(([ipa, kaldi]) => [kaldi, ipa])
);

/**
 * Convert IPA phoneme to Kaldi format
 * @param {string} ipa - IPA phoneme symbol
 * @returns {string} Kaldi phoneme format
 */
export function ipaToKaldi(ipa) {
  if (!ipa) return '';
  const lower = ipa.toLowerCase();
  return IPA_TO_KALDI[lower] || ipa;
}

/**
 * Convert Kaldi phoneme to IPA format
 * @param {string} kaldi - Kaldi phoneme (e.g., 'ae_phon')
 * @returns {string} IPA symbol
 */
export function kaldiToIpa(kaldi) {
  if (!kaldi) return '';
  const lower = kaldi.toLowerCase();
  return KALDI_TO_IPA[lower] || kaldi;
}

/**
 * Convert a phrase with IPA symbols to Kaldi phoneme format
 * @param {string} phrase - Phrase with IPA symbols (e.g., "k æ t cat")
 * @returns {string} Phrase with Kaldi phonemes (e.g., "k_phon ae_phon t_phon cat")
 */
export function convertPhraseIpaToKaldi(phrase) {
  if (!phrase) return '';
  
  return phrase.split(' ').map(token => {
    // If it's already in _phon format, leave it
    if (token.toLowerCase().endsWith('_phon')) {
      return token;
    }
    // Otherwise, try to convert
    return ipaToKaldi(token);
  }).join(' ');
}

/**
 * Convert a transcript with Kaldi phonemes to IPA format
 * @param {string} transcript - Transcript with Kaldi phonemes
 * @returns {string} Transcript with IPA symbols
 */
export function convertTranscriptKaldiToIpa(transcript) {
  if (!transcript) return '';
  
  return transcript.split(' ').map(token => {
    // If it ends with _phon, convert it
    if (token.toLowerCase().endsWith('_phon')) {
      return kaldiToIpa(token);
    }
    // Otherwise leave as is
    return token;
  }).join(' ');
}
